import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import {
  DashboardConfig,
  DashboardTheme,
  Group,
  Tile,
  Tab,
  SearchResult,
  CreateGroupRequest,
  CreateTileRequest,
  CreateTabRequest,
  UpdateGroupRequest,
  UpdateTileRequest,
  UpdateTabRequest,
  ApiResponse
} from '../types';
import { dashboardApi } from '../utils/api';

// Dashboard Context Types
export interface DashboardContextType {
  // State
  config: DashboardConfig | null;
  isLoading: boolean;
  isDirty: boolean;
  error: string | null;
  
  // Config operations
  loadConfig: () => Promise<void>;
  saveConfig: () => Promise<void>;
  resetConfig: () => Promise<void>;
  updateConfig: (config: DashboardConfig) => Promise<void>;
  
  // Tab operations
  activeTab: Tab | null;
  createTab: (tab: CreateTabRequest) => Promise<void>;
  updateTab: (id: string, updates: UpdateTabRequest) => Promise<void>;
  deleteTab: (id: string) => Promise<void>;
  reorderTabs: (sourceIndex: number, destIndex: number) => void;
  setActiveTab: (tabId: string) => Promise<void>;
  
  // Group operations
  createGroup: (group: CreateGroupRequest) => Promise<void>;
  updateGroup: (id: string, updates: UpdateGroupRequest) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
  reorderGroups: (sourceIndex: number, destIndex: number) => void;
  
  // Tile operations
  createTile: (groupId: string, tile: CreateTileRequest) => Promise<void>;
  updateTile: (id: string, updates: UpdateTileRequest) => Promise<void>;
  deleteTile: (id: string) => Promise<void>;
  moveTile: (tileId: string, targetGroupId: string, targetIndex?: number) => void;
  reorderTilesInGroup: (groupId: string, sourceIndex: number, destIndex: number) => void;
  
  // Search
  searchResults: SearchResult[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  performSearch: (query: string) => Promise<void>;
  clearSearch: () => void;
  
  // Theme
  updateTheme: (theme: Partial<DashboardTheme>) => Promise<void>;
  
  // UI state
  editMode: boolean;
  toggleEditMode: () => void;
  selectedItem: { type: 'group' | 'tile' | 'tab'; id: string } | null;
  setSelectedItem: (item: { type: 'group' | 'tile' | 'tab'; id: string } | null) => void;
}

// Dashboard State
interface DashboardState {
  config: DashboardConfig | null;
  isLoading: boolean;
  isDirty: boolean;
  error: string | null;
  searchResults: SearchResult[];
  searchQuery: string;
  editMode: boolean;
  selectedItem: { type: 'group' | 'tile' | 'tab'; id: string } | null;
  activeTab: Tab | null;
}

// Dashboard Actions
type DashboardAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CONFIG'; payload: DashboardConfig }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_DIRTY'; payload: boolean }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_SEARCH_RESULTS'; payload: SearchResult[] }
  | { type: 'CLEAR_SEARCH' }
  | { type: 'TOGGLE_EDIT_MODE' }
  | { type: 'SET_SELECTED_ITEM'; payload: { type: 'group' | 'tile' | 'tab'; id: string } | null }
  | { type: 'SET_ACTIVE_TAB'; payload: Tab | null }
  | { type: 'ADD_TAB'; payload: Tab }
  | { type: 'UPDATE_TAB'; payload: { id: string; updates: Partial<Tab> } }
  | { type: 'DELETE_TAB'; payload: string }
  | { type: 'REORDER_TABS'; payload: { sourceIndex: number; destIndex: number } }
  | { type: 'UPDATE_GROUP'; payload: { id: string; updates: Partial<Group> } }
  | { type: 'DELETE_GROUP'; payload: string }
  | { type: 'ADD_GROUP'; payload: Group }
  | { type: 'REORDER_GROUPS'; payload: { sourceIndex: number; destIndex: number } }
  | { type: 'UPDATE_TILE'; payload: { id: string; updates: Partial<Tile> } }
  | { type: 'DELETE_TILE'; payload: string }
  | { type: 'ADD_TILE'; payload: { groupId: string; tile: Tile } }
  | { type: 'MOVE_TILE'; payload: { tileId: string; targetGroupId: string; targetIndex?: number } }
  | { type: 'REORDER_TILES'; payload: { groupId: string; sourceIndex: number; destIndex: number } }
  | { type: 'UPDATE_THEME'; payload: Partial<DashboardTheme> };

// Initial state
const initialState: DashboardState = {
  config: null,
  isLoading: false,
  isDirty: false,
  error: null,
  searchResults: [],
  searchQuery: '',
  editMode: false,
  selectedItem: null,
  activeTab: null,
};

// Dashboard reducer
function dashboardReducer(state: DashboardState, action: DashboardAction): DashboardState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_CONFIG':
      return { ...state, config: action.payload, isDirty: false, error: null };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    
    case 'SET_DIRTY':
      return { ...state, isDirty: action.payload };
    
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    
    case 'SET_SEARCH_RESULTS':
      return { ...state, searchResults: action.payload };
    
    case 'CLEAR_SEARCH':
      return { ...state, searchQuery: '', searchResults: [] };
    
    case 'TOGGLE_EDIT_MODE':
      return { ...state, editMode: !state.editMode, selectedItem: null };
    
    case 'SET_SELECTED_ITEM':
      return { ...state, selectedItem: action.payload };
    
    case 'SET_ACTIVE_TAB':
      return { 
        ...state, 
        activeTab: action.payload,
        config: state.config ? {
          ...state.config,
          activeTabId: action.payload?.id || state.config.activeTabId
        } : state.config
      };
    
    case 'ADD_TAB':
      if (!state.config) return state;
      const newTabs = state.config.tabs ? [...state.config.tabs, action.payload] : [action.payload];
      return {
        ...state,
        config: {
          ...state.config,
          tabs: newTabs,
          activeTabId: state.config.activeTabId || action.payload.id,
          metadata: {
            ...state.config.metadata,
            totalTabs: newTabs.length,
            lastModified: new Date().toISOString()
          }
        },
        activeTab: state.activeTab || action.payload,
        isDirty: true
      };
    
    case 'UPDATE_TAB':
      if (!state.config || !state.config.tabs) return state;
      const updatedTabs = state.config.tabs.map(tab =>
        tab.id === action.payload.id ? { ...tab, ...action.payload.updates } : tab
      );
      return {
        ...state,
        config: {
          ...state.config,
          tabs: updatedTabs,
          metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
        },
        activeTab: state.activeTab?.id === action.payload.id 
          ? { ...state.activeTab, ...action.payload.updates }
          : state.activeTab,
        isDirty: true
      };
    
    case 'DELETE_TAB':
      if (!state.config || !state.config.tabs) return state;
      const filteredTabs = state.config.tabs.filter(tab => tab.id !== action.payload);
      const newActiveTabId = state.config.activeTabId === action.payload 
        ? (filteredTabs[0]?.id)
        : state.config.activeTabId;
      const newActiveTab = filteredTabs.find(tab => tab.id === newActiveTabId) || null;
      
      return {
        ...state,
        config: {
          ...state.config,
          tabs: filteredTabs,
          activeTabId: newActiveTabId,
          metadata: {
            ...state.config.metadata,
            totalTabs: filteredTabs.length,
            lastModified: new Date().toISOString()
          }
        },
        activeTab: newActiveTab,
        isDirty: true
      };
    
    case 'REORDER_TABS':
      if (!state.config || !state.config.tabs) return state;
      const tabsArray = [...state.config.tabs];
      const [movedTab] = tabsArray.splice(action.payload.sourceIndex, 1);
      tabsArray.splice(action.payload.destIndex, 0, movedTab);
      
      // Update order values
      const reorderedTabs = tabsArray.map((tab, index) => ({ ...tab, order: index }));
      
      return {
        ...state,
        config: {
          ...state.config,
          tabs: reorderedTabs,
          metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
        },
        isDirty: true
      };
    
    case 'ADD_GROUP':
      if (!state.config) return state;
      
      // Handle both tab-based and legacy group structures
      if (state.config.tabs && state.activeTab) {
        const updatedTabs = state.config.tabs.map(tab =>
          tab.id === state.activeTab!.id
            ? { ...tab, groups: [...tab.groups, action.payload] }
            : tab
        );
        
        return {
          ...state,
          config: {
            ...state.config,
            tabs: updatedTabs,
            metadata: {
              ...state.config.metadata,
              totalGroups: state.config.metadata.totalGroups + 1,
              lastModified: new Date().toISOString()
            }
          },
          activeTab: state.activeTab 
            ? { ...state.activeTab, groups: [...state.activeTab.groups, action.payload] }
            : state.activeTab,
          isDirty: true
        };
      }
      
      // Legacy structure
      return {
        ...state,
        config: {
          ...state.config,
          groups: [...state.config.groups, action.payload],
          metadata: {
            ...state.config.metadata,
            totalGroups: state.config.groups.length + 1,
            lastModified: new Date().toISOString()
          }
        },
        isDirty: true
      };
    
    case 'UPDATE_GROUP':
      if (!state.config) return state;
      
      // Handle both tab-based and legacy group structures
      if (state.config.tabs && state.activeTab) {
        // Only update the active tab, not all tabs
        const updatedTabs = state.config.tabs.map(tab => {
          if (tab.id === state.activeTab!.id) {
            return {
              ...tab,
              groups: tab.groups.map(group =>
                group.id === action.payload.id ? { ...group, ...action.payload.updates } : group
              )
            };
          }
          return tab;
        });
        
        return {
          ...state,
          config: {
            ...state.config,
            tabs: updatedTabs,
            metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
          },
          activeTab: state.activeTab ? {
            ...state.activeTab,
            groups: state.activeTab.groups.map(group =>
              group.id === action.payload.id ? { ...group, ...action.payload.updates } : group
            )
          } : state.activeTab,
          isDirty: true
        };
      }
      
      // Legacy structure
      return {
        ...state,
        config: {
          ...state.config,
          groups: state.config.groups.map(group =>
            group.id === action.payload.id ? { ...group, ...action.payload.updates } : group
          ),
          metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
        },
        isDirty: true
      };
    
    case 'DELETE_GROUP':
      if (!state.config) return state;
      
      // Handle both tab-based and legacy group structures
      if (state.config.tabs && state.activeTab) {
        // Only update the active tab, not all tabs
        const updatedTabs = state.config.tabs.map(tab => {
          if (tab.id === state.activeTab!.id) {
            return {
              ...tab,
              groups: tab.groups.filter(group => group.id !== action.payload)
            };
          }
          return tab;
        });
        
        // Calculate total tiles across all tabs
        const totalTiles = updatedTabs.reduce((tabSum, tab) => 
          tabSum + tab.groups.reduce((groupSum, group) => groupSum + group.tiles.length, 0), 0
        );
        
        const totalGroups = updatedTabs.reduce((sum, tab) => sum + tab.groups.length, 0);
        
        return {
          ...state,
          config: {
            ...state.config,
            tabs: updatedTabs,
            metadata: {
              ...state.config.metadata,
              totalGroups,
              totalTiles,
              lastModified: new Date().toISOString()
            }
          },
          activeTab: state.activeTab ? {
            ...state.activeTab,
            groups: state.activeTab.groups.filter(group => group.id !== action.payload)
          } : state.activeTab,
          isDirty: true
        };
      }
      
      // Legacy structure
      const filteredGroups = state.config.groups.filter(group => group.id !== action.payload);
      const totalTiles = filteredGroups.reduce((sum, group) => sum + group.tiles.length, 0);
      return {
        ...state,
        config: {
          ...state.config,
          groups: filteredGroups,
          metadata: {
            ...state.config.metadata,
            totalGroups: filteredGroups.length,
            totalTiles,
            lastModified: new Date().toISOString()
          }
        },
        isDirty: true
      };
    
    case 'REORDER_GROUPS':
      if (!state.config) return state;
      const groups = [...state.config.groups];
      const [movedGroup] = groups.splice(action.payload.sourceIndex, 1);
      groups.splice(action.payload.destIndex, 0, movedGroup);
      
      // Update order values
      const reorderedGroups = groups.map((group, index) => ({ ...group, order: index }));
      
      return {
        ...state,
        config: {
          ...state.config,
          groups: reorderedGroups,
          metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
        },
        isDirty: true
      };
    
    case 'ADD_TILE':
      if (!state.config) return state;
      
      // Handle both tab-based and legacy group structures
      if (state.config.tabs && state.activeTab) {
        // Only update the active tab, not all tabs
        const updatedTabs = state.config.tabs.map(tab => {
          if (tab.id === state.activeTab!.id) {
            return {
              ...tab,
              groups: tab.groups.map(group =>
                group.id === action.payload.groupId
                  ? { ...group, tiles: [...group.tiles, action.payload.tile] }
                  : group
              )
            };
          }
          return tab;
        });
        
        return {
          ...state,
          config: {
            ...state.config,
            tabs: updatedTabs,
            metadata: {
              ...state.config.metadata,
              totalTiles: state.config.metadata.totalTiles + 1,
              lastModified: new Date().toISOString()
            }
          },
          activeTab: {
            ...state.activeTab,
            groups: state.activeTab.groups.map(group =>
              group.id === action.payload.groupId
                ? { ...group, tiles: [...group.tiles, action.payload.tile] }
                : group
            )
          },
          isDirty: true
        };
      }
      
      // Legacy structure
      return {
        ...state,
        config: {
          ...state.config,
          groups: state.config.groups.map(group =>
            group.id === action.payload.groupId
              ? { ...group, tiles: [...group.tiles, action.payload.tile] }
              : group
          ),
          metadata: {
            ...state.config.metadata,
            totalTiles: state.config.metadata.totalTiles + 1,
            lastModified: new Date().toISOString()
          }
        },
        isDirty: true
      };
    
    case 'UPDATE_TILE':
      if (!state.config) return state;
      
      // Handle both tab-based and legacy group structures
      if (state.config.tabs && state.activeTab) {
        // Only update the active tab, not all tabs
        const updatedTabs = state.config.tabs.map(tab => {
          if (tab.id === state.activeTab!.id) {
            return {
              ...tab,
              groups: tab.groups.map(group => ({
                ...group,
                tiles: group.tiles.map(tile =>
                  tile.id === action.payload.id ? { ...tile, ...action.payload.updates } : tile
                )
              }))
            };
          }
          return tab;
        });
        
        return {
          ...state,
          config: {
            ...state.config,
            tabs: updatedTabs,
            metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
          },
          activeTab: {
            ...state.activeTab,
            groups: state.activeTab.groups.map(group => ({
              ...group,
              tiles: group.tiles.map(tile =>
                tile.id === action.payload.id ? { ...tile, ...action.payload.updates } : tile
              )
            }))
          },
          isDirty: true
        };
      }
      
      // Legacy structure
      return {
        ...state,
        config: {
          ...state.config,
          groups: state.config.groups.map(group => ({
            ...group,
            tiles: group.tiles.map(tile =>
              tile.id === action.payload.id ? { ...tile, ...action.payload.updates } : tile
            )
          })),
          metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
        },
        isDirty: true
      };
    
    case 'DELETE_TILE':
      if (!state.config) return state;
      
      // Handle both tab-based and legacy group structures
      if (state.config.tabs && state.activeTab) {
        // Only update the active tab, not all tabs
        const updatedTabs = state.config.tabs.map(tab => {
          if (tab.id === state.activeTab!.id) {
            return {
              ...tab,
              groups: tab.groups.map(group => ({
                ...group,
                tiles: group.tiles.filter(tile => tile.id !== action.payload)
              }))
            };
          }
          return tab;
        });
        
        return {
          ...state,
          config: {
            ...state.config,
            tabs: updatedTabs,
            metadata: {
              ...state.config.metadata,
              totalTiles: state.config.metadata.totalTiles - 1,
              lastModified: new Date().toISOString()
            }
          },
          activeTab: {
            ...state.activeTab,
            groups: state.activeTab.groups.map(group => ({
              ...group,
              tiles: group.tiles.filter(tile => tile.id !== action.payload)
            }))
          },
          isDirty: true
        };
      }
      
      // Legacy structure
      return {
        ...state,
        config: {
          ...state.config,
          groups: state.config.groups.map(group => ({
            ...group,
            tiles: group.tiles.filter(tile => tile.id !== action.payload)
          })),
          metadata: {
            ...state.config.metadata,
            totalTiles: state.config.metadata.totalTiles - 1,
            lastModified: new Date().toISOString()
          }
        },
        isDirty: true
      };
    
    case 'MOVE_TILE':
      if (!state.config) return state;
      
      // Handle both tab-based and legacy group structures
      if (state.config.tabs && state.config.tabs.length > 0) {
        // Tab-based structure
        let movedTile: Tile | null = null;
        const newTabs = state.config.tabs.map(tab => ({
          ...tab,
          groups: tab.groups?.map(group => {
            const tileIndex = group.tiles?.findIndex(tile => tile.id === action.payload.tileId) || -1;
            if (tileIndex !== -1) {
              movedTile = group.tiles![tileIndex];
              return {
                ...group,
                tiles: group.tiles?.filter(tile => tile.id !== action.payload.tileId) || []
              };
            }
            return group;
          }) || []
        }));
        
        if (!movedTile) return state;
        
        // Add tile to target group
        const finalTabs = newTabs.map(tab => ({
          ...tab,
          groups: tab.groups?.map(group => {
            if (group.id === action.payload.targetGroupId) {
              const tiles = [...(group.tiles || [])];
              const insertIndex = action.payload.targetIndex ?? tiles.length;
              tiles.splice(insertIndex, 0, { ...movedTile!, order: insertIndex });
              
              // Update order values for all tiles in the group
              return {
                ...group,
                tiles: tiles.map((tile, index) => ({ ...tile, order: index }))
              };
            }
            return group;
          }) || []
        }));
        
        return {
          ...state,
          config: {
            ...state.config,
            tabs: finalTabs,
            metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
          },
          isDirty: true
        };
      } else {
        // Legacy groups structure
        let movedTile: Tile | null = null;
        const groupsAfterRemoval = state.config.groups.map(group => {
          const tileIndex = group.tiles.findIndex(tile => tile.id === action.payload.tileId);
          if (tileIndex !== -1) {
            movedTile = group.tiles[tileIndex];
            return {
              ...group,
              tiles: group.tiles.filter(tile => tile.id !== action.payload.tileId)
            };
          }
          return group;
        });
        
        if (!movedTile) return state;
        
        // Add tile to target group
        const finalGroups = groupsAfterRemoval.map(group => {
          if (group.id === action.payload.targetGroupId) {
            const tiles = [...group.tiles];
            const insertIndex = action.payload.targetIndex ?? tiles.length;
            tiles.splice(insertIndex, 0, { ...movedTile!, order: insertIndex });
            
            // Update order values for all tiles in the group
            return {
              ...group,
              tiles: tiles.map((tile, index) => ({ ...tile, order: index }))
            };
          }
          return group;
        });
        
        return {
          ...state,
          config: {
            ...state.config,
            groups: finalGroups,
            metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
          },
          isDirty: true
        };
      }
    
    case 'REORDER_TILES':
      if (!state.config) return state;
      
      // Handle both tab-based and legacy group structures
      if (state.config.tabs && state.config.tabs.length > 0) {
        // Tab-based structure
        const newTabs = state.config.tabs.map(tab => ({
          ...tab,
          groups: tab.groups?.map(group => {
            if (group.id === action.payload.groupId) {
              const tiles = [...(group.tiles || [])];
              const [movedTile] = tiles.splice(action.payload.sourceIndex, 1);
              tiles.splice(action.payload.destIndex, 0, movedTile);
              
              // Update order values
              return {
                ...group,
                tiles: tiles.map((tile, index) => ({ ...tile, order: index }))
              };
            }
            return group;
          }) || []
        }));
        
        return {
          ...state,
          config: {
            ...state.config,
            tabs: newTabs,
            metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
          },
          isDirty: true
        };
      } else {
        // Legacy groups structure
        return {
          ...state,
          config: {
            ...state.config,
            groups: state.config.groups.map(group => {
              if (group.id === action.payload.groupId) {
                const tiles = [...group.tiles];
                const [movedTile] = tiles.splice(action.payload.sourceIndex, 1);
                tiles.splice(action.payload.destIndex, 0, movedTile);
                
                // Update order values
                return {
                  ...group,
                  tiles: tiles.map((tile, index) => ({ ...tile, order: index }))
                };
              }
              return group;
            }),
            metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
          },
          isDirty: true
        };
      }
    
    case 'UPDATE_THEME':
      if (!state.config) return state;
      return {
        ...state,
        config: {
          ...state.config,
          theme: { ...state.config.theme, ...action.payload },
          metadata: { ...state.config.metadata, lastModified: new Date().toISOString() }
        },
        isDirty: true
      };
    
    default:
      return state;
  }
}

// Create Dashboard Context
export const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

// Dashboard Provider
export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);

  // Helper function to migrate legacy config to tabs structure
  const migrateConfigToTabs = (config: DashboardConfig): DashboardConfig => {
    // If already has tabs, return as is
    if (config.tabs && config.tabs.length > 0) {
      // Set active tab
      const activeTab = config.tabs.find(tab => tab.id === config.activeTabId) || config.tabs[0];
      dispatch({ type: 'SET_ACTIVE_TAB', payload: activeTab });
      return config;
    }

    // Migrate legacy groups to tabs structure
    const mainTab: Tab = {
      id: 'main-dashboard',
      name: 'Dashboard',
      icon: 'dashboard',
      color: '#1976d2',
      order: 0,
      groups: config.groups || []
    };

    const migratedConfig: DashboardConfig = {
      ...config,
      activeTabId: mainTab.id,
      tabs: [mainTab],
      metadata: {
        ...config.metadata,
        version: '2.0',
        totalTabs: 1,
        lastModified: new Date().toISOString()
      }
    };

    dispatch({ type: 'SET_ACTIVE_TAB', payload: mainTab });
    return migratedConfig;
  };

  // Load dashboard configuration
  const loadConfig = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const config = await dashboardApi.getConfig();
      const migratedConfig = migrateConfigToTabs(config);
      dispatch({ type: 'SET_CONFIG', payload: migratedConfig });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Save dashboard configuration
  const saveConfig = useCallback(async () => {
    if (!state.config || !state.isDirty) return;
    
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await dashboardApi.updateConfig(state.config);
      dispatch({ type: 'SET_DIRTY', payload: false });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.config, state.isDirty]);

  // Reset dashboard configuration
  const resetConfig = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const config = await dashboardApi.resetConfig();
      dispatch({ type: 'SET_CONFIG', payload: config });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Update dashboard configuration
  const updateConfig = useCallback(async (config: DashboardConfig) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await dashboardApi.updateConfig(config);
      dispatch({ type: 'SET_CONFIG', payload: config });
    } catch (error: any) {
      console.error('updateConfig error:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Group operations
  const createGroup = useCallback(async (groupData: CreateGroupRequest) => {
    try {
      const newGroup = await dashboardApi.createGroup(groupData);
      dispatch({ type: 'ADD_GROUP', payload: newGroup });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  const updateGroup = useCallback(async (id: string, updates: UpdateGroupRequest) => {
    try {
      const updatedGroup = await dashboardApi.updateGroup(id, updates);
      dispatch({ type: 'UPDATE_GROUP', payload: { id, updates } });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  const deleteGroup = useCallback(async (id: string) => {
    try {
      await dashboardApi.deleteGroup(id);
      dispatch({ type: 'DELETE_GROUP', payload: id });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  const reorderGroups = useCallback((sourceIndex: number, destIndex: number) => {
    dispatch({ type: 'REORDER_GROUPS', payload: { sourceIndex, destIndex } });
  }, []);

  // Tile operations
  const createTile = useCallback(async (groupId: string, tileData: CreateTileRequest) => {
    try {
      const newTile = await dashboardApi.createTile(groupId, tileData);
      dispatch({ type: 'ADD_TILE', payload: { groupId, tile: newTile } });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error; // Re-throw so the dialog knows there was an error
    }
  }, []);

  const updateTile = useCallback(async (id: string, updates: UpdateTileRequest) => {
    try {
      await dashboardApi.updateTile(id, updates);
      dispatch({ type: 'UPDATE_TILE', payload: { id, updates } });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error; // Re-throw so the dialog knows there was an error
    }
  }, []);

  const deleteTile = useCallback(async (id: string) => {
    try {
      await dashboardApi.deleteTile(id);
      dispatch({ type: 'DELETE_TILE', payload: id });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  const moveTile = useCallback((tileId: string, targetGroupId: string, targetIndex?: number) => {
    dispatch({ type: 'MOVE_TILE', payload: { tileId, targetGroupId, targetIndex } });
  }, []);

  const reorderTilesInGroup = useCallback((groupId: string, sourceIndex: number, destIndex: number) => {
    dispatch({ type: 'REORDER_TILES', payload: { groupId, sourceIndex, destIndex } });
  }, []);

  // Tab operations
  const createTab = useCallback(async (tabData: CreateTabRequest) => {
    try {
      const newTab = await dashboardApi.createTab(tabData);
      dispatch({ type: 'ADD_TAB', payload: newTab });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  const updateTab = useCallback(async (id: string, updates: UpdateTabRequest) => {
    try {
      const updatedTab = await dashboardApi.updateTab(id, updates);
      dispatch({ type: 'UPDATE_TAB', payload: { id, updates } });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  const deleteTab = useCallback(async (id: string) => {
    try {
      await dashboardApi.deleteTab(id);
      dispatch({ type: 'DELETE_TAB', payload: id });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  const reorderTabs = useCallback((sourceIndex: number, destIndex: number) => {
    dispatch({ type: 'REORDER_TABS', payload: { sourceIndex, destIndex } });
  }, []);

  const setActiveTab = useCallback(async (tabId: string) => {
    try {
      await dashboardApi.setActiveTab(tabId);
      const activeTab = state.config?.tabs?.find(tab => tab.id === tabId) || null;
      dispatch({ type: 'SET_ACTIVE_TAB', payload: activeTab });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [state.config?.tabs]);

  // Search operations
  const setSearchQuery = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  }, []);

  const performSearch = useCallback(async (query: string) => {
    try {
      const results = await dashboardApi.searchTiles(query);
      dispatch({ type: 'SET_SEARCH_RESULTS', payload: results });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  const clearSearch = useCallback(() => {
    dispatch({ type: 'CLEAR_SEARCH' });
  }, []);

  // Theme operations
  const updateTheme = useCallback(async (theme: Partial<DashboardTheme>) => {
    try {
      const updatedTheme = await dashboardApi.updateTheme(theme);
      dispatch({ type: 'UPDATE_THEME', payload: updatedTheme });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  // UI operations
  const toggleEditMode = useCallback(() => {
    dispatch({ type: 'TOGGLE_EDIT_MODE' });
  }, []);

  const setSelectedItem = useCallback((item: { type: 'group' | 'tile' | 'tab'; id: string } | null) => {
    dispatch({ type: 'SET_SELECTED_ITEM', payload: item });
  }, []);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Auto-save when dirty (with debounce)
  useEffect(() => {
    if (state.isDirty) {
      const timeoutId = setTimeout(() => {
        saveConfig();
      }, 2000); // Auto-save after 2 seconds of inactivity

      return () => clearTimeout(timeoutId);
    }
  }, [state.isDirty, saveConfig]);

  const contextValue: DashboardContextType = {
    // State
    config: state.config,
    isLoading: state.isLoading,
    isDirty: state.isDirty,
    error: state.error,
    
    // Config operations
    loadConfig,
    saveConfig,
    resetConfig,
    updateConfig,
    
    // Tab operations
    activeTab: state.activeTab,
    createTab,
    updateTab,
    deleteTab,
    reorderTabs,
    setActiveTab,
    
    // Group operations
    createGroup,
    updateGroup,
    deleteGroup,
    reorderGroups,
    
    // Tile operations
    createTile,
    updateTile,
    deleteTile,
    moveTile,
    reorderTilesInGroup,
    
    // Search
    searchResults: state.searchResults,
    searchQuery: state.searchQuery,
    setSearchQuery,
    performSearch,
    clearSearch,
    
    // Theme
    updateTheme,
    
    // UI state
    editMode: state.editMode,
    toggleEditMode,
    selectedItem: state.selectedItem,
    setSelectedItem,
  };

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

// Custom hook to use Dashboard Context
export const useDashboard = (): DashboardContextType => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};